<template>
  <base-wrapper :loading="loading">
    <div class="space-y-4">
      <create-order-form
        with-destination-area
        restock-type-label="Metode Penjualan"
        :with-loan="false"
        with-order-balance
        with-reference-code
        with-address-receipt
        with-custom-price
        :disabled-date="false"
        v-model="order"
      />

      <cashback-order-provider
        v-if="order.destinationOffice.id"
        class="space-y-4"
        office-type="pusat"
        :office-code="order.destinationOffice.code"
        :total-price="totalPrice"
        :area-code="order.area.code"
        :shipment-type="order.shipment_type"
        v-model="cashback"
        v-slot="{ minOrder: minCashback }"
      >
        <create-order-detail-form
          :order-id="order.id"
          :origin-office="{ id: me.office_id, code: me.office_code }"
          :origin-warehouse="order.originWarehouse"
          :destination-office="order.destinationOffice"
          :buyer-type="order.buyerType"
          :area="order.area"
          :stock-free-use-global="false"
          :min-spend="minOrderSpend"
          with-cashback-warning
          with-global-stock
          :min-cashback="minCashback"
          :columns="[
            'code',
            'name',
            'point',
            'stock',
            'stock_free',
            'qty',
            'price',
            'total_price',
            'action',
            'total_weight',
            'point_value',
            'bonus_value',
          ]"
          total-price-label=""
          :create-order-attributes="{
            is_using_stock: order.using_stock,
            order_shipment: order.shipment_type,
            reference_code: order.referenceCode,
            orderedAt: order.date,
            order_type: 'sale',
            custom_price: order.customPrice,
            ...(order.discountType ? { discount_type: order.discountType } : {}),
            destination_warehouse_id:
              order.destinationOffice.currentWarehouse.id,
            ...(order.custom_shipment_address
              ? {
                  receipt_name: order.shipmentAddress.receipt_name,
                  receipt_phone: order.shipmentAddress.receipt_phone,
                  destination_address: {
                    province_id: order.shipmentAddress.province.id,
                    city_id: order.shipmentAddress.city.id,
                    district_id: order.shipmentAddress.district.id,
                    village_id: order.shipmentAddress.village.id,
                    address: order.shipmentAddress.address,
                  },
                }
              : {}),
          }"
          v-model="orderDetails"
          @order-created="onOrderCreated"
          @order-detail-updated="onOrderDetailUpdated"
        />

        <template v-if="totalPrice > 0">
          <order-promo-list
            v-if="order.using_stock && productBundles.length"
            :order-details="orderDetailsForPromoList"
            :product-bundles="productBundles"
            :stocks="productBundlesBonusStocks"
            :warehouse-stocks="productBundlesBonusWarehouseStocks"
            @change-selected-bundles="onChangeSelectedBundles"
            @change-choosen-bonuses="onChangeChoosenBonuses"
            @update-remainder-status="status => promoHasRemainder = status"
          />

          <create-order-summary
            :order-details="orderDetails"
            :cashback="cashback"
            :payment-methods="paymentMethods"
            v-model="order"
          />

          <create-payment-form
            v-model="paymentMethods"
            :order-id="order.id"
            :payment-id="paymentId"
            auto-save
            sync
            :filter-reserved="false"
            :filter-payment-method-type="{
              'filter[not-name]': 'Cashback',
            }"
            :stockist-balance="order.destinationOffice.order_balance"
            :with-validate-button="false"
            :with-summary="false"
            :edc-input-bank="false"
            @saving="(val) => (loadingUpdatePayment = val)"
            @focus-amount="focusingAmount = true"
            @focus-amount-out="focusingAmount = false"
          />
        </template>

        <div class="flex justify-end gap-2">
          <base-button
            :disabled="
              loadingUpdatePayment ||
              !detailsValid ||
              !paymentValid ||
              (productBundles.length > 0 && promoHasRemainder)
            "
            :title="promoHasRemainder ? 'Ada promo yang bisa dipilih' : ''"
            @click="visibleValidate = true"
          >
            Siap Validasi            
          </base-button
          >
          <base-button color="danger" :to="{ name: 'penjualan' }"
            >Selesai</base-button
          >
        </div>
      </cashback-order-provider>
    </div>

    <validate-order-confirmation
      :order-id="order.id"
      :visible="visibleValidate"
      :total-price="totalPrice"
      :min-price="minOrderSpend"
      with-min-price-warning
      :validation-data="{
        selected_bundles_id: this.selectedBundes,
        choosen_bonuses: this.choosenBonuses
      }"
      @close="visibleValidate = false"
      @confirmed="visibleDetail = true"
    />
    <view-order-pusat-modal
      :order-id="order.id"
      :visible="visibleDetail"
      @close="$router.push({ name: 'penjualan' })"
      @refresh="$router.push({ name: 'penjualan' })"
    />
  </base-wrapper>
</template>

<script>
import CreateOrderForm from '@/components/order/create-order-form.vue';
import CreateOrderSummary from '@/components/order/create-order-summary.vue';
import CreateOrderDetailForm from '@/components/order/create-order-detail-form.vue';
import CashbackOrderProvider from '@/components/order/cashback-order-provider.vue';
import CreatePaymentForm from '@/components/payment/create-payment-form.vue';
import ValidateOrderConfirmation from '@/components/order/validate-order-confirmation.vue';
import ViewOrderPusatModal from '@/components/order/view-order-pusat-modal.vue';
import OrderPromoList from '@/components/promo/OrderPromoList.vue';
import { requestMixin } from '@/mixins/request/request';
import { mapGetters } from 'vuex';
import { toCurrency } from '@/services/currency.service';
import dayjs from 'dayjs';

export default {
  mixins: [requestMixin],
  components: {
    CreateOrderForm,
    CreateOrderDetailForm,
    CashbackOrderProvider,
    CreatePaymentForm,
    ValidateOrderConfirmation,
    CreateOrderSummary,
    ViewOrderPusatModal,
    OrderPromoList
  },
  data() {
    return {
      cashback: null,
      loadingBuyerType: false,
      loadingDefaultOriginWarehouse: false,
      loadingOrder: false,
      loadingSetup: false,
      loadingPaymentId: false,
      loadingUpdatePayment: false,
      focusingAmount: false,
      minOrderSpend: 0,
      order: {
        areaStatus: null,
        area: {
          id: null,
          code: null,
        },
        buyerType: {
          id: null,
          code: null,
        },
        custom_shipment_address: false,
        customPrice: false,
        discountType: false,
        discountPrice: 0,
        discountPriceValue: 0,
        discountPriceInput: 0,
        date: new Date(),
        createdDate: new Date(),
        shippingFee: 0,
        plasticQty: 0,
        originOfficeType: 'pusat',
        destinationOfficeType: 'stockist',
        notes: null,
        destinationOffice: {
          address: null,
          area: {
            id: null,
            code: null,
          },
          code: null,
          hasAddress: false,
          currentWarehouse: {
            id: null,
            code: null,
          },
          id: null,
          name: null,
          phone: null,
          originalCode: null,
          order_balance: null,
          searchCode: null,
        },
        id: null,
        originWarehouse: {
          area: {
            id: null,
            code: null,
          },
          code: null,
          id: null,
          name: null,
          originalCode: null,
          searchCode: null,
          officeId: null
        },
        shipment_type: 'pickup',
        referenceCode: null,
        referenceCodeSaved: false,
        shipmentAddress: {
          receipt_name: null,
          receipt_phone: null,
          province: {
            id: null,
            name: null,
          },
          city: {
            id: null,
            name: null,
          },
          district: {
            id: null,
            name: null,
          },
          village: {
            id: null,
            name: null,
          },
          address: null,
          area: {
            id: null,
            code: null,
          },
        },
        using_stock: true,
        valid: false,
      },
      orderDetails: [
        {
          id: null,
          productId: null,
          productCode: null,
          originalProductCode: null,
          productName: null,
          isPoint: null,
          stock: null,
          stock_free: null,
          qty: null,
          originalQty: null,
          price: null,
          totalPrice: null,
          totalWeight: null,
          pointValue: null,
          bonusValue: null,
        },
      ],
      paymentId: null,
      paymentMethods: [
        {
          paymentMethodId: null,
          paymentMethodTypeId: null,
          amount: null,
          officeBankId: null,
          receiptNumber: null,
        },
      ],
      productBundles: [],
      productBundlesBonusStocks: {},
      productBundlesBonusWarehouseStocks: {},
      selectedBundes: [],
      choosenBonuses: [],
      visibleValidate: false,
      visibleDetail: false,
      promoHasRemainder: true
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
    }),
    loading() {
      return (
        this.loadingBuyerType ||
        this.loadingDefaultOriginWarehouse ||
        this.loadingOrder ||
        this.loadingSetup ||
        this.loadingPaymentId
      );
    },
    totalPrice() {
      const subTotalPrice = this.orderDetails.reduce(
        (total, orderDetail) => total + orderDetail.totalPrice,
        0
      );

      if (!this.order.customPrice || !this.order.discountType) {
        return subTotalPrice
      }

      return subTotalPrice - this.order.discountPrice
    },
    detailsValid() {
      const details = this.orderDetails.filter((orderDetail) => orderDetail.id);

      return details.length && details.every((orderDetail) => orderDetail.qty);
    },
    paymentValid() {
      return this.paymentMethods
        .filter((paymentMethod) => paymentMethod.paymentMethodTypeId)
        .every((paymentMethod) => !!paymentMethod.amount);
    },
    orderDetailsForPromoList() {
      return this.orderDetails
        .filter(item => item.productId)
        .map(item => ({
          productId: item.productId,
          qty: item.qty,
          productCode: item.productCode 
        }))
    },
  },
  methods: {
    dayjs,
    async loadBuyerType() {
      this.loadingBuyerType = true;

      const [res, err] = await this.request('/api/v1/buyer-types', {
        params: {
          'filter[code]': 10,
          'fields[buyer-types]': 'code',
        },
      });

      if (!err && res.data.length) {
        const buyerType = res.data[0];

        this.order.buyerType.id = buyerType.id;
        this.order.buyerType.code = buyerType.attributes.code;
      }

      this.loadingBuyerType = false;
    },
    async loadDefaultOriginWarehouse() {
      this.loadingDefaultOriginWarehouse = true;

      const [warehouse, err] = await this.request(
        `/api/v1/warehouses/${this.me.current_warehouse}`,
        {
          params: {
            include: 'area',
            'fields[warehouses]': 'code,name,area',
            'fields[areas]': 'code',
          },
        }
      );

      if (!err) {
        this.order.originWarehouse.id = warehouse.data.id;
        this.order.originWarehouse.code = warehouse.data.attributes.code;
        this.order.originWarehouse.name = warehouse.data.attributes.name;
        this.order.originWarehouse.officeId = this.me.office_id

        const area = this.getSingleIncluded(
          warehouse,
          warehouse.data.relationships.area.data.id
        );

        this.order.originWarehouse.area.id = area.id;
        this.order.originWarehouse.area.code = area.attributes.code;

        this.order.area.id = area.id;
        this.order.area.code = area.attributes.code;

        this.order.originWarehouse.searchCode = `${warehouse.data.attributes.code} (${area.attributes.code})`;
        this.order.originWarehouse.originalCode = `${warehouse.data.attributes.code} (${area.attributes.code})`;
      }

      this.loadingDefaultOriginWarehouse = false;
    },
    async loadOrder() {
      this.loadingOrder = true;

      const [order, err] = await this.request(
        `/api/v1/orders/${this.$route.params.id}`,
        {
          params: {
            include:
              'area,buyer-type,destination-warehouse,destination-office,origin-office,origin-warehouse,order-details',
            'fields[orders]':
              'area_status,custom_price,discount_type,discount_price,sub_total_price,grand_total_price,createdAt,orderedAt,order_shipment,notes,reference_code,is_valid_for_payment,area,buyer-type,destination-office,destination-warehouse,origin-office,origin-warehouse,order-details,shipment_address,final_price,shipping_fee,plastic_qty,receipt_name,receipt_phone',
            'fields[areas]': 'code',
            'fields[offices]':
              'code,name,address,office_type,area_code,order_balance_available',
            'fields[warehouses]': 'code,name',
            'fields[order-details]':
              'product_id,product_code,product_name,current_stock,current_stock_free,product_qty,product_price,total_price,is_point,total_weight,point_value,bonus_value',
          },
        }
      );

      if (!err) {
        this.order.id = order.data.id;

        this.order.areaStatus = order.data.attributes.area_status
        this.order.customPrice = order.data.attributes.custom_price
        this.order.discountType = order.data.attributes.discount_type
        this.order.discountPrice = order.data.attributes.discount_price

        if (this.order.discountType === 'input') {
          const discountInput = Math.floor(order.data.attributes.discount_price / order.data.attributes.grand_total_price * 100)

          this.order.discountPriceInput = discountInput
          this.order.discountPriceValue = discountInput
        }

        this.order.shippingFee = order.data.attributes.shipping_fee;
        this.order.plasticQty = order.data.attributes.plastic_qty;

        this.order.notes = order.data.attributes.notes;

        const area = this.getSingleIncluded(
          order,
          order.data.relationships.area.data.id
        );
        const buyerType = this.getSingleIncluded(
          order,
          order.data.relationships['buyer-type'].data.id
        );
        const destinationOffice = this.getSingleIncluded(
          order,
          order.data.relationships['destination-office'].data.id
        );
        const destinationWarehouse = this.getSingleIncluded(
          order,
          order.data.relationships['destination-warehouse'].data.id
        );
        const originOffice = this.getSingleIncluded(
          order,
          order.data.relationships['origin-office'].data.id
        );
        const originWarehouse = this.getSingleIncluded(
          order,
          order.data.relationships['origin-warehouse'].data.id
        );

        this.order.area.id = area.id;
        this.order.area.code = area.attributes.code;

        this.order.buyerType.id = buyerType.id;
        this.order.buyerType.code = buyerType.attributes.code;

        this.order.custom_shipment_address = false;
        this.order.shipmentAddress.address =
          destinationOffice.attributes.address ||
          order.data.attributes.shipment_address;
        this.order.shipmentAddress.receipt_name =
          order.data.attributes.receipt_name;
        this.order.shipmentAddress.receipt_phone =
          order.data.attributes.receipt_phone;

        this.order.createdDate = order.data.attributes.createdAt;
        this.order.date = order.data.attributes.orderedAt;

        this.order.destinationOffice.id = destinationOffice.id;
        this.order.destinationOffice.code = destinationOffice.attributes.code;
        this.order.destinationOffice.phone =
          destinationOffice.attributes.cellphone;
        this.order.destinationOffice.originalCode =
          destinationOffice.attributes.code;
        this.order.destinationOffice.searchCode =
          destinationOffice.attributes.code;
        this.order.destinationOffice.name = destinationOffice.attributes.name;
        this.order.destinationOffice.order_balance =
          destinationOffice.attributes.order_balance_available;

        this.order.destinationOffice.address =
          destinationOffice.attributes.address;

        this.order.destinationOffice.currentWarehouse.id =
          destinationWarehouse.id;
        this.order.destinationOffice.currentWarehouse.code =
          destinationWarehouse.attributes.code;

        this.order.originWarehouse.area.id = null;
        this.order.originWarehouse.area.code =
          originOffice.attributes.area_code;

        this.order.originWarehouse.code = originWarehouse.attributes.code;
        this.order.originWarehouse.id = originWarehouse.id;
        this.order.originWarehouse.name = originWarehouse.attributes.name;
        this.order.originWarehouse.originalCode =
          originWarehouse.attributes.code;
        this.order.originWarehouse.searchCode = originWarehouse.attributes.code;
        this.order.originWarehouse.officeId = this.me.office_id;

        this.order.shipment_type = order.data.attributes.order_shipment;
        this.order.referenceCode = order.data.attributes.reference_code;
        this.order.referenceCodeSaved = true;

        this.order.valid = order.data.attributes.is_valid_for_payment;

        const orderDetails = this.getIncludedByType(order, 'order-details');

        this.orderDetails = orderDetails.map((orderDetail) => ({
          id: orderDetail.id,
          productId: orderDetail.attributes.product_id,
          productCode: orderDetail.attributes.product_code,
          originalProductCode: orderDetail.attributes.product_code,
          productName: orderDetail.attributes.product_name,
          isPoint: orderDetail.attributes.is_point,
          stock: orderDetail.attributes.current_stock,
          stock_free: orderDetail.attributes.current_stock_free,
          qty: orderDetail.attributes.product_qty || null,
          originalQty: orderDetail.attributes.product_qty || null,
          price: orderDetail.attributes.product_price,
          totalPrice: orderDetail.attributes.total_price,
          totalWeight: orderDetail.attributes.total_weight,
          pointValue: orderDetail.attributes.point_value,
          bonusValue: orderDetail.attributes.bonus_value,
        }));

        this.orderDetails.push({
          id: null,
          productId: null,
          productCode: null,
          originalProductCode: null,
          productName: null,
          isPoint: null,
          stock: null,
          stock_free: null,
          qty: null,
          originalQty: null,
          price: null,
          totalPrice: null,
          totalWeight: null,
          pointValue: null,
          bonusValue: null,
        });

        this.loadProductBundles()
      }

      this.loadingOrder = false;
    },
    async loadPaymentId(id) {
      this.loadingPaymentId = true;

      const [res, err] = await this.request(
        `/api/v1/orders/${id || this.$route.params.id}/payments`,
        {
          params: {
            'fields[payments]': 'code',
            'page[size]': 1,
          },
        }
      );

      if (!err && res.data.length) {
        this.paymentId = res.data[0].id;

        await this.loadPaymentMethods();
      }

      this.loadingPaymentId = false;
    },
    async loadPaymentMethods() {
      const [res, err] = await this.request(
        `/api/v1/payments/${this.paymentId}/payment-methods`,
        {
          params: {
            include: 'payment-method-type,office-bank',
            'fields[payment-methods]':
              'payment_amount,receipt_number,payment-method-type,office-bank,payment_method_type',
            'fields[payment-method-types]': 'name',
            'fields[office-banks]': 'bank_name',
          },
        }
      );

      if (!err) {
        const paymentMethods = res.data.filter(
          (paymentMethod) =>
            paymentMethod.attributes.payment_method_type !== 'Cashback'
        );

        if (paymentMethods.length) {
          this.paymentMethods = paymentMethods.map((paymentMethod) => {
            const paymentMethodType =
              paymentMethod.relationships['payment-method-type'].data;
            const officeBank = paymentMethod.relationships['office-bank'].data;

            return {
              paymentMethodId: paymentMethod.id,
              paymentMethodTypeId: paymentMethodType
                ? paymentMethodType.id
                : null,
              amount: toCurrency(paymentMethod.attributes.payment_amount),
              officeBankId: officeBank ? officeBank.id : null,
              receiptNumber: paymentMethod.attributes.receipt_number,
              saved: true
            };
          });

          const orderBalanceUsed = paymentMethods.find(
            (paymentMethod) =>
              paymentMethod.attributes.payment_method_type === 'Saldo Stockist'
          );

          if (orderBalanceUsed) {
            this.order.destinationOffice.order_balance +=
              orderBalanceUsed.attributes.payment_amount;
          }
        }
      }
    },
    async loadProductBundles() {
      const [res, err] = await this.request(`/api/v1/orders/${this.order.id}/product-bundles`, {
        params: {
          sort: 'sort_order'
        }
      })

      if (!err) {
        this.productBundles = res.data
        this.productBundlesBonusStocks = res.meta.free_stocks
        this.productBundlesBonusWarehouseStocks = res.meta.warehouse_free_stocks
      }
    },
    async loadSetup() {
      this.loadingSetup = true;

      const [res, err] = await this.request('/api/v1/setups', {
        params: {
          'filter[setup_key]': 'min_order_spend_for_stockist',
          'fields[setups]': 'setup_value',
        },
      });

      if (!err && res.data.length) {
        this.minOrderSpend = +res.data[0].attributes.setup_value;
      }

      this.loadingSetup = false;
    },
    onOrderCreated(order) {
      this.order.id = order.data.id;

      this.loadPaymentId(order.data.id);

      this.$router.push({
        name: 'penjualan.edit',
        params: {
          id: order.data.id,
        },
      });
    },
    onOrderDetailUpdated(data) {
      this.order.shippingFee = data.shippingFee;
      this.order.plasticQty = data.plasticQty;

      if (this.order.customPrice && this.order.discountType === 'non_bv') {
        this.order.discountPrice = data.discountPrice;
      }

      this.loadProductBundles()
    },
    onChangeSelectedBundles(selectedBundes) {
      const copySelectedBundles = [...selectedBundes]

      copySelectedBundles.sort(([, selectedAtA], [, selectedAtB]) => selectedAtA - selectedAtB)

      this.selectedBundes = copySelectedBundles.map(([bundleId]) => bundleId)
    },
    onChangeChoosenBonuses(choosenBonuses) {
      this.choosenBonuses = {...choosenBonuses}
    }
  },
  created() {
    this.loadSetup();

    if (this.$route.params.id) {
      this.loadOrder();
      this.loadPaymentId();
    } else {
      this.loadBuyerType();
      this.loadDefaultOriginWarehouse();
    }
  },
};
</script>
